/**
 * Creature Detail Modal Component
 *
 * Displays comprehensive creature information when a player clicks on a creature card.
 * Shows sprite, name, race/class, stats, abilities, and size.
 */

import React, { useEffect, useRef } from 'react';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import './CreatureDetailModal.css';

interface CreatureDetailModalProps {
  creature: SerializedCreatureDocument | null;
  onClose: () => void;
}

export const CreatureDetailModal: React.FC<CreatureDetailModalProps> = ({
  creature,
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (creature) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [creature, onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!creature) {
    return null;
  }

  // Format size for display
  const sizeMultiplier = creature.size ?? 1.0;
  const sizeCategory = getSizeCategory(sizeMultiplier);
  const sizeDisplay = `${sizeCategory} (${sizeMultiplier.toFixed(1)}x)`;

  // Get abilities from combatAttributes (these have priority and full data)
  const abilities = creature.combatAttributes.map(attr => ({
    name: attr.name,
    category: attr.category,
    damageType: attr.damageType,
    attackType: attr.attackType,
    priority: attr.priority,
    icon: getAbilityIcon(attr.damageType, attr.attackType)
  }));

  // Sort abilities by priority (highest first)
  abilities.sort((a, b) => b.priority - a.priority);

  return (
    <div
      className="creature-detail-backdrop"
      ref={modalRef}
      onClick={handleBackdropClick}
    >
      <div className="creature-detail-modal">
        {/* Close Button */}
        <button className="modal-close-button" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* Sprite Display */}
        <div className="detail-sprite-container">
          <img
            src={creature.sprites.menuSprite}
            alt={creature.name}
            className="detail-sprite"
          />
        </div>

        {/* Creature Name */}
        <h2 className="detail-name">{creature.name}</h2>

        {/* Race and Class */}
        <div className="detail-race-class">
          <span className="detail-race">Race: {capitalize(creature.race)}</span>
          <span className="detail-separator">|</span>
          <span className="detail-class">Class: {capitalize(creature.class)}</span>
        </div>

        {/* Stats Section */}
        <div className="detail-section">
          <h3 className="detail-section-title">Stats</h3>
          <div className="detail-stats-grid">
            {/* Note: Actual combat stats are derived dynamically during combat.
                For gallery display, we show placeholder values based on archetype.
                Real stats come from combat engine's stat derivation system. */}
            <div className="detail-stat">
              <span className="stat-icon">❤️</span>
              <span className="stat-label">HP:</span>
              <span className="stat-value">{getEstimatedHP(creature)}</span>
            </div>
            <div className="detail-stat">
              <span className="stat-icon">⚔️</span>
              <span className="stat-label">Attack:</span>
              <span className="stat-value">{getEstimatedAttack(creature)}</span>
            </div>
            <div className="detail-stat">
              <span className="stat-icon">🛡️</span>
              <span className="stat-label">Defense:</span>
              <span className="stat-value">{getEstimatedDefense(creature)}</span>
            </div>
            <div className="detail-stat">
              <span className="stat-icon">⚡</span>
              <span className="stat-label">Speed:</span>
              <span className="stat-value">{getEstimatedSpeed(creature)}</span>
            </div>
          </div>
        </div>

        {/* Abilities Section */}
        <div className="detail-section">
          <h3 className="detail-section-title">Abilities</h3>
          <div className="detail-abilities-grid">
            {abilities.map((ability, index) => (
              <div key={index} className="detail-ability">
                <span className="ability-icon">{ability.icon}</span>
                <span className="ability-name">{ability.name}</span>
                <span className="ability-type">{ability.attackType}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Size Section */}
        <div className="detail-section">
          <h3 className="detail-section-title">Size</h3>
          <div className="detail-size">
            {sizeDisplay}
          </div>
        </div>

        {/* Concept (from Claude Vision) */}
        <div className="detail-concept">
          <em>"{creature.concept}"</em>
        </div>
      </div>
    </div>
  );
};

/**
 * Get size category label from multiplier
 */
function getSizeCategory(multiplier: number): string {
  if (multiplier <= 0.5) return 'Tiny';
  if (multiplier <= 0.8) return 'Small';
  if (multiplier <= 1.2) return 'Medium';
  if (multiplier <= 1.8) return 'Large';
  if (multiplier <= 2.5) return 'Huge';
  return 'Colossal';
}

/**
 * Get ability icon based on damage type and attack type
 */
function getAbilityIcon(damageType: string, attackType: string): string {
  // Damage type icons
  const damageIcons: Record<string, string> = {
    'fire': '🔥',
    'ice': '❄️',
    'lightning': '⚡',
    'poison': '🧪',
    'dark': '🌑',
    'holy': '✨',
    'physical': '⚔️'
  };

  // Attack type icons
  const attackIcons: Record<string, string> = {
    'melee': '🗡️',
    'ranged': '🏹',
    'spell': '📜',
    'aoe': '💥',
    'buff': '📈',
    'debuff': '📉',
    'heal': '💚'
  };

  // Prefer damage type icon, fall back to attack type
  return damageIcons[damageType.toLowerCase()] || attackIcons[attackType.toLowerCase()] || '✦';
}

/**
 * Capitalize first letter of a string
 */
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Estimate HP based on creature archetype
 * Note: Real stats are derived dynamically in combat engine
 */
function getEstimatedHP(creature: SerializedCreatureDocument): number {
  // Base HP varies by class/concept
  const concept = creature.concept.toLowerCase();
  const classType = creature.class.toLowerCase();

  // Tank-like creatures
  if (concept.includes('tank') || concept.includes('guardian') ||
      classType.includes('tank') || classType.includes('guardian')) {
    return 150;
  }

  // Warrior/Fighter types
  if (concept.includes('warrior') || concept.includes('knight') ||
      classType.includes('warrior') || classType.includes('fighter')) {
    return 120;
  }

  // Mage/caster types (lower HP)
  if (concept.includes('mage') || concept.includes('wizard') ||
      classType.includes('mage') || classType.includes('wizard')) {
    return 80;
  }

  // Default medium HP
  return 100;
}

/**
 * Estimate attack based on abilities
 */
function getEstimatedAttack(creature: SerializedCreatureDocument): number {
  // Higher priority abilities generally mean higher attack
  const maxPriority = Math.max(...creature.combatAttributes.map(a => a.priority), 0);

  if (maxPriority >= 9) return 45;
  if (maxPriority >= 8) return 40;
  if (maxPriority >= 7) return 35;
  if (maxPriority >= 6) return 30;
  return 25;
}

/**
 * Estimate defense based on class/concept
 */
function getEstimatedDefense(creature: SerializedCreatureDocument): number {
  const concept = creature.concept.toLowerCase();
  const classType = creature.class.toLowerCase();

  // Heavy armor types
  if (concept.includes('tank') || concept.includes('guardian') ||
      classType.includes('tank') || classType.includes('guardian') ||
      concept.includes('knight') || classType.includes('paladin')) {
    return 30;
  }

  // Medium armor
  if (concept.includes('warrior') || concept.includes('fighter')) {
    return 20;
  }

  // Light armor (mages, rogues, rangers)
  return 10;
}

/**
 * Estimate speed based on class/concept
 */
function getEstimatedSpeed(creature: SerializedCreatureDocument): number {
  const concept = creature.concept.toLowerCase();
  const classType = creature.class.toLowerCase();

  // Fast units (rogues, scouts, light units)
  if (concept.includes('rogue') || concept.includes('scout') ||
      classType.includes('rogue') || classType.includes('assassin') ||
      concept.includes('swift') || concept.includes('quick')) {
    return 8;
  }

  // Slow units (tanks, heavy armor)
  if (concept.includes('tank') || concept.includes('guardian') ||
      classType.includes('tank')) {
    return 4;
  }

  // Medium speed
  return 6;
}
